import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/format';
import { Separator } from '@/components/ui/separator';
import type { BookingDto } from '@/api/types';

type PriceBreakdownProps = {
  booking: BookingDto;
};

export default function PriceBreakdown({ booking }: PriceBreakdownProps) {
  const { t, i18n } = useTranslation();
  const { pricing, extensions, originalDuration } = booking;
  const currency = pricing.currency;
  const hasExtensions = extensions && extensions.length > 0;
  const origDuration = originalDuration || booking.duration;

  // Calculate original session price by subtracting extension prices from subtotal
  const extensionTotal = extensions?.reduce((sum, ext) => sum + ext.price, 0) || 0;
  const originalSessionPrice = pricing.subtotal - extensionTotal;

  return (
    <div className="space-y-2 text-sm text-left">
      <Separator />

      {/* Original session */}
      <div className="flex justify-between">
        <span>
          {t('booking.unit_price', { duration: origDuration })}
          {hasExtensions && (
            <span className="ml-1 text-xs text-muted-foreground">{t('booking.original_label')}</span>
          )}
        </span>
        <span>{formatCurrency(originalSessionPrice + (pricing.discount || 0), currency, i18n.language)}</span>
      </div>

      {/* VIP discount */}
      {(pricing.discount ?? 0) > 0 && (
        <div className="flex justify-between text-green-600">
          <span>{t('booking.vip_discount')}</span>
          <span>-{formatCurrency(pricing.discount!, currency, i18n.language)}</span>
        </div>
      )}

      {/* Extension line items */}
      {extensions?.map((ext) => (
        <div key={ext.id} className="flex justify-between text-muted-foreground">
          <span>
            {t('booking.extension_minutes', { minutes: ext.additionalMinutes })}
            {ext.creditApplied > 0 && (
              <span className="ml-1 text-green-600 text-xs">({t('credit.credit_applied').toLowerCase()})</span>
            )}
          </span>
          <span>{formatCurrency(ext.price, currency, i18n.language)}</span>
        </div>
      ))}

      {/* Addon items */}
      {pricing.addonItems?.map((item) => (
        <div key={item.addonId} className="flex justify-between text-muted-foreground">
          <span>{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</span>
          <span>{formatCurrency(item.subtotal, currency, i18n.language)}</span>
        </div>
      ))}

      {/* Service fee */}
      <div className="flex justify-between text-muted-foreground">
        <span>{t('booking.platform_fee')}</span>
        <span>{formatCurrency(pricing.platformFee, currency, i18n.language)}</span>
      </div>

      {/* Tax */}
      <div className="flex justify-between text-muted-foreground">
        <span>{t('booking.tax')}</span>
        <span>{formatCurrency(pricing.tax, currency, i18n.language)}</span>
      </div>

      <Separator />

      {/* Total */}
      <div className="flex justify-between font-semibold">
        <span>{t('booking.total')}</span>
        <span>{formatCurrency(pricing.total, currency, i18n.language)}</span>
      </div>

      {/* Deposit */}
      {pricing.depositAmount != null && pricing.depositAmount > 0 && (
        <>
          <Separator />
          <div className="flex justify-between text-amber-700">
            <span>{t('booking.deposit')}</span>
            <span>{formatCurrency(pricing.depositAmount, currency, i18n.language)}</span>
          </div>
          <p className="text-xs text-muted-foreground">{t('booking.deposit_refund_notice')}</p>
        </>
      )}
    </div>
  );
}
